import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getPasswordResetKeyInfo, resetPassword } from "../library/allauth";
import { Lock, Eye, EyeOff, CheckCircle, ArrowLeft } from "lucide-react";
import XRoadz_Logo from "../img/XRoadz_Logo.png";

export default function ResetPassword() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const key = params.get("key");

  const [keyValid, setKeyValid] = useState(null); // null=checking, true=ok, false=invalid

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!key) {
      setKeyValid(false);
      return;
    }
    getPasswordResetKeyInfo(key).then((res) => {
      if (res.status === 200) {
        setKeyValid(true);
      } else {
        setKeyValid(false);
        setError("Invalid or expired reset link. Please request a new one.");
      }
    });
  }, [key]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!key) {
      setError("Invalid or expired reset link. Please request a new one.");
      return;
    }
    if (!password) {
      setError("Please enter a new password.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const res = await resetPassword({ key, password });
      if (res.status === 200 || res.status === 401) {
        setDone(true);
        setTimeout(() => navigate("/mfa-setup?required=true"), 3000);
      } else {
        setError(
          res.errors?.[0]?.message ||
            "Failed to reset password. The link may have expired.",
        );
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        .a1 { animation: fadeUp 0.5s ease forwards; }
        .a2 { animation: fadeUp 0.5s ease 0.15s forwards; opacity:0; }
      `}</style>

      <div className="min-h-screen flex">
        {/* Left Panel */}
        <div className="hidden lg:flex w-1/2 flex-col justify-between p-14 relative overflow-hidden bg-emerald-700">
          <div className="absolute w-[500px] h-[500px] rounded-full bg-emerald-600 opacity-20 -top-32 -left-32" />
          <div className="absolute w-[300px] h-[300px] rounded-full bg-emerald-900 opacity-20 bottom-20 right-0" />

          <div className="relative z-10 flex items-center gap-4 mt-6">
            <div className="flex items-center gap-4 mt-6">
              <div className="h-12 bg-white rounded-xl flex items-center justify-center shadow-sm px-3 overflow-hidden">
                <img
                  src={XRoadz_Logo}
                  alt="XRoadz"
                  className="h-7 object-contain"
                />
              </div>
              <div>
                <p className="text-white font-bold text-xl leading-tight">
                  XRoadz WebPortal
                </p>
                <p className="text-emerald-200 text-xs opacity-70 tracking-widest uppercase">
                  Traffic Analytics & Roadway Safety
                </p>
              </div>
            </div>
          </div>

          <div className="relative z-10 max-w-md">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-yellow-300 border-opacity-40 mb-6">
              <span className="w-2 h-2 rounded-full bg-yellow-300 animate-pulse" />
              <span className="text-yellow-200 text-xs tracking-widest uppercase font-medium">
                Secure Password Reset
              </span>
            </div>
            <h1 className="text-white text-5xl font-black leading-tight mb-6 tracking-tight">
              Set a new
              <br />
              <span className="text-emerald-200">strong</span> password.
            </h1>
            <p className="text-emerald-100 text-sm leading-relaxed opacity-80">
              Choose a password that is at least 8 characters long. You will be
              redirected to login after resetting.
            </p>
          </div>

          <div className="relative z-10">
            <p className="text-emerald-200 text-xs tracking-widest uppercase opacity-50">
              © {new Date().getFullYear()} XRoadz Inc. — Innovating Traffic
              Safety
            </p>
          </div>
        </div>

        {/* Right Panel */}
        <div className="w-full lg:w-1/2 flex items-center justify-center bg-gray-50 px-8 py-12">
          <div className="w-full max-w-lg">
            <div className="flex lg:hidden justify-center mb-8">
              <img
                src={XRoadz_Logo}
                alt="XRoadz"
                className="h-10 object-contain"
              />
            </div>

            {done ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-12 shadow-sm text-center a1">
                <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={32} className="text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Password set successfully!
                </h2>
                <p className="text-sm text-gray-400 mb-6">
                  Redirecting you to set up two-factor authentication...
                </p>
                <a
                  href="/login"
                  className="inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:text-emerald-800 font-medium"
                >
                  <ArrowLeft size={14} /> Back to Login
                </a>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-200 p-12 shadow-sm">
                <div className="mb-8 a1">
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">
                    Set your new password
                  </h2>
                  <p className="text-sm text-gray-400">
                    Choose a strong password for your account.
                  </p>
                </div>

                {!key && (
                  <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 text-center">
                    Invalid or expired reset link. Please{" "}
                    <a
                      href="/forgot-password"
                      className="underline font-medium"
                    >
                      request a new one
                    </a>
                    .
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5 a2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      New password
                    </label>
                    <div className="relative">
                      <Lock
                        size={15}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter new password"
                        className="w-full pl-9 pr-10 py-3 text-sm text-gray-900 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent placeholder-gray-400 transition-colors"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      Confirm password
                    </label>
                    <div className="relative">
                      <Lock
                        size={15}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type={showConfirm ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        className={`w-full pl-9 pr-10 py-3 text-sm text-gray-900 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent placeholder-gray-400 transition-colors ${
                          confirmPassword && password !== confirmPassword
                            ? "border-red-300"
                            : "border-gray-200"
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirm(!showConfirm)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {confirmPassword && password !== confirmPassword && (
                      <p className="text-xs text-red-500 mt-1">
                        Passwords do not match
                      </p>
                    )}
                  </div>

                  {error && <p className="text-sm text-red-500">{error}</p>}

                  <button
                    type="submit"
                    disabled={loading || !keyValid}
                    className="w-full py-3 text-sm font-semibold text-white bg-emerald-700 rounded-lg hover:bg-emerald-800 transition-colors disabled:opacity-60"
                  >
                    {loading ? "Setting password..." : "Set New Password"}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <a
                    href="/login"
                    className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <ArrowLeft size={14} /> Back to Login
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
